import React from 'react';
import { Ruler } from 'lucide-react';
import { tx } from '@/translations/translations';
import { displaySize } from '@/utils/displaySize';

export const swatchClass = (isSelected, isOOS) => {
  if (isOOS) {
    return isSelected
      ? 'border-cyan-500 bg-cyan-50 text-slate-500 line-through'
      : 'border-slate-200 bg-slate-50 text-slate-400 line-through hover:border-slate-300';
  }
  return isSelected
    ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
    : 'border-slate-200 hover:border-slate-300 text-slate-600';
};

const variantBtnClass = (isSelected, isOOS) =>
  `inline-flex items-center justify-center min-h-[34px] px-3 rounded-lg border-2 text-xs font-medium transition-all ${isSelected ? 'border-cyan-500 bg-cyan-50 text-cyan-700' : 'border-slate-200 text-slate-600'} ${isOOS ? 'opacity-40 line-through' : ''}`;

export const traditionDefaultLabel = (trad, language) => {
  if (trad === 'ashkenazi') return tx(language, 'Ashkenazi', 'Ashkenazi', 'אשכנזי');
  if (trad === 'sefaradi') return tx(language, 'Sefaradí', 'Sephardic', 'ספרדי');
  return tx(language, 'No Amarrados', 'Not Tied', 'לא קשורים');
};

export const ColorSelector = ({ product, language, selectedColor, onSelect, outOfStockColors, forceShowOptions }) => {
  if ((product.removedSelections || []).includes('colors')) return null;
  const hiddenColors = product.hiddenColors || [];
  const colors = (product.colors || []).filter(c => !hiddenColors.includes(c.value));
  if (colors.length === 0) return null;
  const hasAvailable = colors.some(c => !outOfStockColors.includes(c.value) && !c.outOfStock);
  if (!forceShowOptions && !hasAvailable) return null;
  return (
    <div className="mt-4 sm:mt-6">
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
        {tx(language, 'Color', 'Color', 'צבע')}
      </label>
      <div className="flex flex-wrap gap-2">
        {colors.map((color) => {
          const isOOS = outOfStockColors.includes(color.value) || color.outOfStock;
          const isSelected = selectedColor?.value === color.value && selectedColor?.name === color.name;
          return (
            <button
              key={`color-${color.value}`}
              onClick={() => onSelect(color)}
              className={`inline-flex items-center justify-center gap-2 min-h-[34px] px-3 rounded-lg border-2 text-xs font-medium transition-all ${swatchClass(isSelected, isOOS)}`}
              data-testid={`color-swatch-${color.value.replace(/[^a-zA-Z0-9]/g, '')}`}
            >
              <div className="w-3 h-3 rounded-full border border-slate-300" style={{ backgroundColor: color.value }} />
              {color.name}
              {isOOS && <span className="ml-1 text-[9px]">({tx(language, 'Agotado', 'Sold out', 'אזל')})</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const CottonTypeSelector = ({ product, language, selectedCottonType, onSelect, outOfStockCottonTypes }) => {
  if (!product.hasCottonType) return null;
  if ((product.removedSelections || []).includes('cotton')) return null;
  const hiddenCT = product.hiddenCottonTypes || [];
  const smoothHidden = hiddenCT.includes('cotton_smooth');
  const edgeHidden = hiddenCT.includes('cotton_edge');
  // Show selector only if BOTH options are visible and in stock (otherwise auto-picks the available one)
  if (smoothHidden || edgeHidden) return null;
  const smoothOOS = outOfStockCottonTypes.includes('cotton_smooth');
  const edgeOOS = outOfStockCottonTypes.includes('cotton_edge');
  if (smoothOOS || edgeOOS) return null;
  const btn = (key, img, labelEs, labelEn, oos, testid) => (
    <button
      onClick={() => { if (!oos) onSelect(key); }}
      disabled={oos}
      className={`inline-flex items-center justify-center gap-2 min-h-[34px] px-3 rounded-lg border-2 text-xs font-medium transition-all ${selectedCottonType === key && !oos ? 'border-cyan-500 bg-cyan-50 text-cyan-700' : 'border-slate-200 text-slate-600'} ${oos ? 'opacity-40 line-through cursor-not-allowed' : ''}`}
      data-testid={testid}
    >
      <img src={img} alt="" className="w-5 h-5 rounded object-cover" />
      {tx(language, labelEs, labelEn, labelEs === 'Liso' ? 'חלק' : 'מצולע')}
    </button>
  );
  return (
    <div className="mt-4 sm:mt-6">
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
        {tx(language, 'Tipo de algodón', 'Cotton type', 'סוג כותנה')}
      </label>
      <div className="flex gap-2">
        {btn('cotton_smooth', '/products/cotton-smooth.webp', 'Liso', 'Smooth', smoothOOS, 'cotton-type-smooth')}
        {btn('cotton_edge', '/products/cotton-edge.webp', 'Acanalado', 'Ribbed', edgeOOS, 'cotton-type-edge')}
      </div>
    </div>
  );
};

const getHilosSectionLabel = (productId, hilosIncluded, usesAY, language) => {
  if (productId === 16) return tx(language, 'Hilos Negros', 'Black Threads', 'חוטים שחורים');
  const handmade = tx(language, 'Hilos a mano (A.Y)', 'Handmade Threads (A.Y)', 'חוטי עבודת יד');
  const plain = tx(language, 'Hilos', 'Threads', 'חוטים');
  if (hilosIncluded) return [2, 3, 15].includes(productId) ? handmade : plain;
  return usesAY ? handmade : plain;
};

export const ThreadSelector = ({
  product, language, isKidsSize, isWool, hilosIncluded,
  availableThreads, outOfStockThreads, selectedHilos, setSelectedHilos,
  forceShowOptions, onClose,
}) => {
  if (product.noTzitzit) return null;
  if ((product.removedSelections || []).includes('threads')) return null;
  const mat = product.material;
  const usesAY = isWool || (mat !== 'bambu' && mat !== 'dryfit' && mat !== 'dry-fit' && !isKidsSize);
  // "Incluidos" chip eliminado desde admin (marcado oculto)
  if (hilosIncluded && (product.hiddenThreads || []).includes('incluidos')) return null;
  if (!hilosIncluded && availableThreads.length === 0) return null;
  const hasAYDelgado = availableThreads.includes('avodat_yad_delgado');
  const hasAYGrueso = availableThreads.includes('avodat_yad_grueso');
  const hasSinHilos = availableThreads.includes('sin_hilos');
  const conHilosAvail = availableThreads.some(t => t !== 'sin_hilos' && !outOfStockThreads.includes(t));
  const anyAvailableAdult = (hasAYDelgado && !outOfStockThreads.includes('avodat_yad_delgado')) ||
                            (hasAYGrueso && !outOfStockThreads.includes('avodat_yad_grueso')) ||
                            (hasSinHilos && !outOfStockThreads.includes('sin_hilos'));
  const anyAvailableKids = conHilosAvail || (hasSinHilos && !outOfStockThreads.includes('sin_hilos'));
  if (!forceShowOptions && (isKidsSize ? !anyAvailableKids : !anyAvailableAdult)) return null;

  const goToHalaja = () => {
    onClose();
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('openHubSection', { detail: 'halajot' }));
      const el = document.getElementById('mas-del-tzitzit');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const threadBtn = (key, defaultLabel, testid) => {
    const oos = outOfStockThreads.includes(key);
    return (
      <button
        onClick={() => !oos && setSelectedHilos(key)}
        disabled={oos}
        className={variantBtnClass(selectedHilos === key, oos)}
        data-testid={testid}
      >
        {product.variantOptionLabels?.threads?.[key] || defaultLabel}
      </button>
    );
  };

  const renderOptions = () => {
    if (hilosIncluded) {
      const includedLabel = product.variantOptionLabels?.threads?.incluidos ||
        (product.id === 16
          ? tx(language, 'Negros', 'Black', 'שחורים')
          : tx(language, 'Incluidos', 'Included', 'כלולים'));
      return (
        <button
          type="button"
          className="inline-flex items-center justify-center min-h-[34px] px-3 rounded-lg border-2 text-xs font-medium border-cyan-500 bg-cyan-50 text-cyan-700"
          data-testid="hilos-incluidos"
        >
          {includedLabel}
        </button>
      );
    }
    if (isKidsSize) {
      const sinHilosOOS = outOfStockThreads.includes('sin_hilos');
      return (
        <>
          <button
            onClick={() => setSelectedHilos(availableThreads.find(t => t !== 'sin_hilos' && !outOfStockThreads.includes(t)) || 'avodat_yad_delgado')}
            disabled={!conHilosAvail}
            className={variantBtnClass(selectedHilos !== 'sin_hilos', !conHilosAvail)}
            data-testid="hilos-con-hilos"
          >
            {tx(language, 'Con Hilos', 'With Threads', 'עם חוטים')}
          </button>
          {hasSinHilos && (
            <button
              onClick={() => !sinHilosOOS && setSelectedHilos('sin_hilos')}
              disabled={sinHilosOOS}
              className={variantBtnClass(selectedHilos === 'sin_hilos', sinHilosOOS)}
              data-testid="hilos-sin-hilos"
            >
              {tx(language, 'Sin Hilos', 'No Threads', 'ללא חוטים')}
            </button>
          )}
        </>
      );
    }
    return (
      <>
        {hasAYDelgado && threadBtn('avodat_yad_delgado', tx(language, 'Delgados', 'Thin', 'דקים'), 'hilos-avodat-yad-delgado')}
        {hasAYGrueso && threadBtn('avodat_yad_grueso', tx(language, 'Gruesos', 'Thick', 'עבים'), 'hilos-avodat-yad-grueso')}
        {hasSinHilos && threadBtn('sin_hilos', tx(language, 'Sin Hilos', 'No Threads', 'ללא חוטים'), 'hilos-sin-hilos')}
      </>
    );
  };

  return (
    <div className="mt-4 sm:mt-6">
      <div className="flex items-center justify-between mb-3 gap-2">
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {product.variantLabels?.threads || getHilosSectionLabel(product.id, hilosIncluded, usesAY, language)}
        </label>
        {usesAY && !hilosIncluded && (
          <button
            type="button"
            onClick={goToHalaja}
            className="text-xs font-semibold text-cyan-600 hover:text-cyan-700 transition whitespace-nowrap"
            data-testid="modal-link-halaja"
          >
            {tx(language, 'Ver halajá →', 'View halakhah →', 'לצפייה בהלכה ←')}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {renderOptions()}
      </div>
    </div>
  );
};

export const TraditionSelector = ({
  product, language, hideNoAmarrados, selectedHilos, selectedTradition, setSelectedTradition,
  outOfStockTraditions, forceShowOptions, onClose,
}) => {
  if (product.noTzitzit || selectedHilos === 'sin_hilos') return null;
  if ((product.removedSelections || []).includes('traditions')) return null;
  const hiddenTrads = product.hiddenTraditions || [];
  const traditions = (hideNoAmarrados ? ['sefaradi', 'ashkenazi'] : ['sefaradi', 'ashkenazi', 'no_amarrados']).filter(t => !hiddenTrads.includes(t));
  if (traditions.length === 0) return null;
  const anyAvailable = traditions.some(t => !outOfStockTraditions.includes(t));
  if (!forceShowOptions && !anyAvailable) return null;

  const goToSignificado = () => {
    onClose();
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('openHubSection', { detail: 'significado' }));
      const trySrollToAmarre = (attempts = 0) => {
        const target = document.getElementById('significado-amarre');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (attempts < 10) {
          setTimeout(() => trySrollToAmarre(attempts + 1), 120);
        }
      };
      trySrollToAmarre();
    }, 150);
  };

  return (
    <div className="mt-4 sm:mt-6">
      <div className="flex items-center justify-between mb-3 gap-2">
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {product.variantLabels?.traditions || tx(language, 'Tradición', 'Tradition', 'מסורת')}
        </label>
        <button
          type="button"
          onClick={goToSignificado}
          className="text-xs font-semibold text-cyan-600 hover:text-cyan-700 transition whitespace-nowrap"
          data-testid="modal-link-significado"
        >
          {tx(language, 'Ver significado →', 'View meaning →', 'למשמעות ←')}
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {traditions.map((trad) => {
          const isOOS = outOfStockTraditions.includes(trad);
          const label = product.variantOptionLabels?.traditions?.[trad] || traditionDefaultLabel(trad, language);
          return (
            <button
              key={trad}
              onClick={() => !isOOS && setSelectedTradition(trad)}
              disabled={isOOS}
              className={variantBtnClass(selectedTradition === trad, isOOS)}
              data-testid={`tradition-${trad}`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const CustomSelections = ({ visibleCustomSelections, customChoices, setCustomChoices }) => (
  <>
    {visibleCustomSelections.map(cs => (
      <div key={cs.id} className="mt-4 sm:mt-6">
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
          {cs.name?.es || cs.name}
        </label>
        <div className="flex flex-wrap gap-2">
          {(cs.options || []).map(o => {
            if ((o.state || 'visible') === 'hidden') return null;
            const isOOS = cs.state === 'oos' || o.state === 'oos';
            const isSelected = customChoices[cs.id] === o.key;
            return (
              <button key={o.key} disabled={isOOS}
                onClick={() => setCustomChoices(p => ({ ...p, [cs.id]: o.key }))}
                className={`inline-flex items-center justify-center min-h-[34px] px-3 rounded-lg border-2 text-xs font-medium transition-all ${swatchClass(isSelected, isOOS)}`}
                data-testid={`custom-sel-${cs.id}-${o.key}`}>
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
    ))}
  </>
);

export const SizeSelector = ({ product, language, selectedSize, setSelectedSize, outOfStockSizes, forceShowOptions, onOpenSizeGuide }) => {
  const sizes = product.sizes || [];
  if (sizes.length === 0) return null;
  const anyAvailable = sizes.some(s => !(outOfStockSizes || []).includes(s));
  if (!forceShowOptions && !anyAvailable) return null;
  return (
    <div className="mt-4 sm:mt-6">
      <div className="flex items-center justify-between mb-3 gap-2">
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {tx(language, 'Talla', 'Size', 'מידה')}
        </label>
        <button
          type="button"
          onClick={onOpenSizeGuide}
          className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-600 hover:text-cyan-700 transition whitespace-nowrap"
          data-testid="modal-link-size-guide"
        >
          <Ruler className="h-3 w-3" />
          {tx(language, 'Ver guía de tallas', 'View size guide', 'מדריך מידות')}
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {sizes.map((size) => {
          const sizeOOS = (outOfStockSizes || []).includes(size);
          return (
            <button
              key={size}
              onClick={() => !sizeOOS && setSelectedSize(size)}
              disabled={sizeOOS}
              className={`px-3 py-1.5 rounded-lg border-2 text-xs font-medium transition-all ${selectedSize === size ? 'border-cyan-500 bg-cyan-50 text-cyan-700' : 'border-slate-200 text-slate-600'} ${sizeOOS ? 'opacity-40 line-through' : ''}`}
            >
              {displaySize(size, language)}
            </button>
          );
        })}
      </div>
    </div>
  );
};
